import { MenuItem, Paper, TextField, Typography } from '@mui/material';
import React from 'react';

import { ProgramCatalogItem, ProgramCompletionAction, ProgramVisibility } from '../../api/programs.interface';
import { ProgramManagementListItem } from './programs.types';
import ProgramImageUploadDropzone from './ProgramImageUploadDropzone';
import { buildProgramContentPath, buildProgramSchedulePath, PROGRAM_COMPLETION_ACTION_LABELS, PROGRAM_VISIBILITY_LABELS } from './programs.utils';

interface CatalogErrors {
    completionAction?: string;
    description?: string;
    displayName?: string;
    nextProgramKey?: string;
    sortOrder?: string;
    weeks?: string;
}

interface Props {
    availablePrograms?: ProgramManagementListItem[];
    disabledKey?: boolean;
    errors?: CatalogErrors;
    hideProgramKey?: boolean;
    keyError?: string;
    onCatalogFieldChange: (fieldName: 'displayName' | 'description' | 'imageUrl' | 'nextProgramKey' | 'completionAction', value: string) => void;
    onCatalogImageUploaded: (imageUrl: string) => void;
    onCatalogNumberChange: (fieldName: 'sortOrder' | 'weeks', value: string) => void;
    onCatalogVisibilityChange: (value: ProgramVisibility) => void;
    onProgramKeyChange?: (value: string) => void;
    programCatalog: ProgramCatalogItem;
    programKey: string;
    title?: string;
}

const PROGRAM_COMPLETION_ACTION_VALUES: ProgramCompletionAction[] = ['restart', 'advance', 'stay'];
const PROGRAM_VISIBILITY_VALUES: ProgramVisibility[] = ['draft', 'internal', 'live'];

const ProgramCatalogFields: React.FC<Props> = ({
    availablePrograms = [],
    disabledKey,
    errors,
    hideProgramKey,
    keyError,
    onCatalogFieldChange,
    onCatalogImageUploaded,
    onCatalogNumberChange,
    onCatalogVisibilityChange,
    onProgramKeyChange,
    programCatalog,
    programKey,
    title = 'Program Metadata',
}) => {
    const displayNameError = errors?.displayName || (hideProgramKey ? keyError : undefined);
    const displayNameHelperText = displayNameError || (hideProgramKey ? 'Program key will be generated from the display name.' : undefined);

    return (
        <Paper sx={{ borderRadius: 3, p: 2 }} variant="outlined">
            <Typography sx={{ fontWeight: 700, mb: 1.5 }} variant="subtitle1">{title}</Typography>
            {!hideProgramKey && (
                <TextField
                    disabled={disabledKey}
                    error={Boolean(keyError)}
                    fullWidth
                    helperText={keyError || 'Stable route and API identifier.'}
                    label="Program Key"
                    onChange={(event) => onProgramKeyChange?.(event.target.value)}
                    size="small"
                    sx={{ mb: 1.5 }}
                    value={programKey}
                />
            )}
            <TextField
                error={Boolean(displayNameError)}
                fullWidth
                helperText={displayNameHelperText}
                label="Display Name"
                onChange={(event) => onCatalogFieldChange('displayName', event.target.value)}
                size="small"
                sx={{ mb: 1.5 }}
                value={programCatalog.displayName}
            />
            <TextField
                error={Boolean(errors?.description)}
                fullWidth
                helperText={errors?.description || 'Used by mobile program selection and completion messaging.'}
                label="Program Description"
                multiline
                minRows={3}
                onChange={(event) => onCatalogFieldChange('description', event.target.value)}
                size="small"
                sx={{ mb: 1.5 }}
                value={programCatalog.description}
            />
            <Typography color="text.secondary" sx={{ mb: 0.5 }} variant="body2">Programs path: {buildProgramContentPath(programKey || 'program')}</Typography>
            <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">Schedule path: {buildProgramSchedulePath(programKey || 'program')}</Typography>
            <TextField
                fullWidth
                helperText="Optional manual URL. Uploading a file below will replace this value."
                label="Thumbnail Image URL"
                onChange={(event) => onCatalogFieldChange('imageUrl', event.target.value)}
                size="small"
                sx={{ mb: 1.5 }}
                value={programCatalog.imageUrl || ''}
            />
            <ProgramImageUploadDropzone
                disabled={false}
                imageUrl={programCatalog.imageUrl}
                onImageUploaded={onCatalogImageUploaded}
                title={`${programCatalog.displayName || programKey || 'Program'} Thumbnail`}
            />
            <TextField
                error={Boolean(errors?.weeks)}
                fullWidth
                helperText={errors?.weeks || 'Used by list completeness and workout processing.'}
                label="Configured Weeks"
                onChange={(event) => onCatalogNumberChange('weeks', event.target.value)}
                size="small"
                sx={{ mb: 1.5, mt: 1.5 }}
                value={String(programCatalog.weeks)}
            />
            <TextField
                error={Boolean(errors?.sortOrder)}
                fullWidth
                helperText={errors?.sortOrder || 'Lower values appear first in the program selector.'}
                label="Sort Order"
                onChange={(event) => onCatalogNumberChange('sortOrder', event.target.value)}
                size="small"
                sx={{ mb: 1.5 }}
                value={String(programCatalog.sortOrder)}
            />
            <TextField
                fullWidth
                helperText="Draft stays in admin only, internal is staff preview only, live is public or member-facing."
                label="Visibility"
                onChange={(event) => onCatalogVisibilityChange(event.target.value as ProgramVisibility)}
                select
                size="small"
                sx={{ mb: 1.5 }}
                value={programCatalog.visibility}
            >
                {PROGRAM_VISIBILITY_VALUES.map((visibility) => (
                    <MenuItem key={visibility} value={visibility}>{PROGRAM_VISIBILITY_LABELS[visibility]}</MenuItem>
                ))}
            </TextField>
            <TextField
                error={Boolean(errors?.completionAction)}
                fullWidth
                helperText={errors?.completionAction || 'Controls what mobile should do when the user finishes the final week.'}
                label="Completion Action"
                onChange={(event) => onCatalogFieldChange('completionAction', event.target.value)}
                select
                size="small"
                sx={{ mb: 1.5 }}
                value={programCatalog.completionAction}
            >
                {PROGRAM_COMPLETION_ACTION_VALUES.map((completionAction) => (
                    <MenuItem key={completionAction} value={completionAction}>{PROGRAM_COMPLETION_ACTION_LABELS[completionAction]}</MenuItem>
                ))}
            </TextField>
            {programCatalog.completionAction === 'advance' && (
                <TextField
                    error={Boolean(errors?.nextProgramKey)}
                    fullWidth
                    helperText={errors?.nextProgramKey || 'Choose the program users should move to after completion.'}
                    label="Next Program"
                    onChange={(event) => onCatalogFieldChange('nextProgramKey', event.target.value)}
                    select
                    size="small"
                    sx={{ mb: 1.5 }}
                    value={programCatalog.nextProgramKey || ''}
                >
                    {availablePrograms
                        .filter((item) => item.name !== programKey)
                        .map((item) => (
                            <MenuItem key={item.name} value={item.name}>{item.displayName}</MenuItem>
                        ))}
                </TextField>
            )}
        </Paper>
    );
};

export default ProgramCatalogFields;